import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';

import {
  Heading,
  InternalLink,
  Text,
  animation,
  colors,
  media
} from '../ui-components';

import BackgroundHeroPreview from './preview-components/background-hero-preview';

/**
 * Component that renders a leader study card
 *
 * @component LeaderCard
 * 
 * @param {object} props
 * 
 * @prop {object} leader
 * @prop {string} slug
 * @prop {boolean} isPreview
 */
const LeaderCard = props => {
  const { leader, slug, isPreview } = props;
  const image = isPreview ? leader?.avatar?.url : getImage(leader?.avatar?.fluid);

  const WrapperComponent = leader?.slug ? WrapperLink : WrapperContainer;

  return (
    <WrapperComponent to={leader?.slug ? `/about-us/our-leadership/${leader.slug}` : undefined}>
      <LeaderContainer slug={leader?.slug}>
        {image && <HeroImg id={slug} image={image} alt={leader?.name} objectPosition="top" isPreview={isPreview}/>}
        <Wrapper>
          <Content>
            <Heading level={5}>{leader?.name}</Heading>
            {!leader?.subtitle ? <Text>{leader?.title}</Text> : <Text>{leader?.title + ' and ' + leader.subtitle}</Text>}
            <Text italic>{leader?.officeLocation}</Text>
          </Content>
        </Wrapper>
      </LeaderContainer>
    </WrapperComponent>
  );
};

const WrapperLink = styled(props => <InternalLink {...props} />)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  text-transform: none;
  margin-bottom: 16px;
  width: 100%;
  overflow: hidden;
  align-items: inherit;
  box-shadow: 0px 0px 80px 0px rgba(0, 0, 0, 0.06);
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const WrapperContainer = styled(props => <div {...props} />)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  text-transform: none;
  margin-bottom: 16px;
  width: 100%;
  overflow: hidden;
  align-items: inherit;
  box-shadow: 0px 0px 80px 0px rgba(0, 0, 0, 0.06);
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroImg = styled(props => {
  const Component = props.isPreview ? BackgroundHeroPreview : GatsbyImage;
  return <Component {...props} />;
})`
  width: 100%;
  height: 250px;
  
  @media only screen and (max-width: ${media.mediumTop}) {
    height: 250px;
    width: auto;
  }
  
  @media only screen and (max-width: ${media.smallBottom}) {
    height: 250px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  padding: 28px;
  background-color: ${colors.pureWhite};
  flex: 1 0 auto;
  height: 100%;
`;

const LeaderContainer = styled(props => <div {...props} />)`
  height: 100%;
  width: 100%;
  background-color: ${colors.pureWhite};

  #${({ slug }) => slug } {
    transition: transform ${animation.general.durationAndTiming};
  }

  &:hover {
    #${({ slug }) => slug } {
      transform: ${( slug ) => (slug ? 'scale(1.025);' : '')};
    }

    ${Wrapper} {
      background-color: ${({ slug }) => (slug ? colors.cardBackgroundHover : colors.pureWhite)};
      transition: background-color ${animation.general.durationAndTiming};
      
      h5, p {
        color: ${({ slug }) => (slug ? colors.pureWhite : '')};
        transition: color ${animation.general.durationAndTiming};
      }
    }
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  z-index: 1;
  justify-content: flex-start;

  @media only screen and (max-width: ${media.mediumTop}) {
    text-align: left;
    display: flex;
  }
`;

export default LeaderCard;
